import React, { useState, useEffect, useCallback } from "react";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Card,
  Row,
  Col,
  Space,
  message,
  Alert,
  Spin,
  Image,
  Switch,
} from "antd";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ImageUpload from "../../../components/common/ImageUpload";
import Upload from "../../../components/common/Upload";
import {
  FixtureFormProps,
  FixtureFormData,
  FixtureDimensions,
} from "../../../types/fixture";
import {
  validateField,
  validateFixtureFormWithBusinessRules,
  ValidationError,
} from "../../../utils/fixtureValidation";
import { FixtureService } from "../../../services/fixtureService";

const { Option } = Select;
const { TextArea } = Input;

const FixtureForm: React.FC<FixtureFormProps> = ({
  fixture,
  mode,
  onSave,
  onCancel,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [imagePreview, setImagePreview] = useState<string | undefined>();
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  );
  const [isValidating, setIsValidating] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (fixture && mode === "edit") {
      form.setFieldsValue({
        typeName: fixture.typeName,
        typeCode: fixture.typeCode,
        description: fixture.description,
        dimensions: fixture.dimensions,
        holeCount: fixture.holeCount,
        crossBeamCount: fixture.crossBeamCount,
        supportHold: fixture.supportHold,
        imageUrl: fixture.imageUrl,
      });

      // Set existing image preview if available
      if (fixture.imageUrl) {
        setImagePreview(fixture.imageUrl);
      }
    } else if (mode === "create") {
      // Set default value for supportHold in create mode
      form.setFieldsValue({
        supportHold: false,
        crossBeamCount: 1,
      });
    }
  }, [fixture, mode, form]);

  const handleSubmit = async (values: any) => {
    try {
      setIsValidating(true);
      setSubmitError(null);

      const formData: FixtureFormData = {
        typeName: values.typeName?.trim(),
        typeCode: values.typeCode?.trim().toUpperCase(),
        dimensions: values.dimensions,
        holeCount: values.holeCount,
        crossBeamCount: values.crossBeamCount,
        supportHold: values.supportHold,
        description: values.description?.trim(),
        imageUrl: values.imageUrl,
      };

      // Perform comprehensive validation
      const validation = validateFixtureFormWithBusinessRules(
        formData,
        [], // TODO: Pass existing fixtures for uniqueness check
        fixture?.id
      );

      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setSubmitError(t("fixture.formValidationFailed"));

        // Focus on first error field
        const firstError = validation.errors[0];
        if (firstError) {
          const fieldName = firstError.field.includes(".")
            ? firstError.field.split(".")
            : firstError.field;
          form.scrollToField(fieldName);
        }

        return;
      }

      setValidationErrors([]);

      // Call the parent save handler
      await onSave(formData);

      // Clear unsaved changes flag on successful save
      setHasUnsavedChanges(false);
    } catch (error: any) {
      console.error("Form submission error:", error);

      // Handle different types of errors
      let errorMessage =
        mode === "create"
          ? t("fixture.createFailed")
          : t("fixture.updateFailed");

      if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      setSubmitError(errorMessage);

      // Don't show message here as the parent component will handle it
      // The error will be re-thrown to let parent handle it
      throw error;
    } finally {
      setIsValidating(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      // Could add a confirmation dialog here
      // For now, just call the parent cancel handler
    }
    onCancel();
  };

  // Real-time field validation
  const handleFieldChange = useCallback(
    (changedFields: any[]) => {
      // Mark form as having unsaved changes
      setHasUnsavedChanges(true);

      // Clear submit error when user starts making changes
      if (submitError) {
        setSubmitError(null);
      }

      changedFields.forEach(({ name, value }) => {
        if (name && name.length > 0) {
          const fieldName = Array.isArray(name) ? name.join(".") : name;
          const errors = validateField(fieldName, value);

          // Update validation errors state
          setValidationErrors((prev) => {
            const filtered = prev.filter(
              (error) => !error.field.startsWith(fieldName)
            );
            return [...filtered, ...errors];
          });
        }
      });
    },
    [submitError]
  );

  // Get field-specific validation errors
  const getFieldError = (fieldName: string) => {
    const errors = validationErrors.filter(
      (error) => error.field === fieldName
    );
    return errors.length > 0 ? errors[0].message : undefined;
  };

  // Get nested field validation errors
  const getNestedFieldError = (parentField: string, childField: string) => {
    const fullFieldName = `${parentField}.${childField}`;
    return getFieldError(fullFieldName);
  };

  return (
    <div>
      {submitError && (
        <Alert
          message={t("fixture.submitError")}
          description={submitError}
          type="error"
          showIcon
          closable
          className="fixture-form-error"
          onClose={() => setSubmitError(null)}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFieldsChange={handleFieldChange}
        disabled={loading || isValidating}
      >
        <Card
          title={
            mode === "create"
              ? t("fixture.createTitle")
              : t("fixture.editTitle")
          }
        >
          <Row gutter={24}>
            <Col span={16}>
              <Form.Item
                name="typeName"
                label={t("fixture.typeName")}
                rules={[
                  { required: true, message: t("fixture.typeNameRequired") },
                ]}
                validateStatus={getFieldError("typeName") ? "error" : undefined}
                help={getFieldError("typeName")}
              >
                <Input
                  placeholder={t("fixture.typeNamePlaceholder")}
                  maxLength={50}
                />
              </Form.Item>

              <Form.Item
                name="typeCode"
                label={t("fixture.typeCode")}
                rules={[
                  { required: true, message: t("fixture.typeCodeRequired") },
                ]}
                validateStatus={getFieldError("typeCode") ? "error" : undefined}
                help={getFieldError("typeCode")}
              >
                <Input
                  placeholder={t("fixture.typeCodePlaceholder")}
                  maxLength={20}
                  style={{ textTransform: "uppercase" }}
                  disabled={mode === "edit"}
                />
              </Form.Item>

              <Form.Item label={t("fixture.dimensions")} required>
                <Row gutter={8}>
                  <Col span={7}>
                    <Form.Item
                      name={["dimensions", "width"]}
                      noStyle
                      rules={[
                        { required: true, message: t("fixture.widthRequired") },
                      ]}
                    >
                      <InputNumber
                        placeholder={t("fixture.widthPlaceholder")}
                        addonBefore={t("fixture.widthPlaceholder")}
                        min={0.1}
                        precision={2}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={7}>
                    <Form.Item
                      name={["dimensions", "height"]}
                      label={t("fixture.height")}
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: t("fixture.heightRequired"),
                        },
                      ]}
                    >
                      <InputNumber
                        placeholder={t("fixture.heightPlaceholder")}
                        addonBefore={t("fixture.heightPlaceholder")}
                        min={0.1}
                        precision={2}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={7}>
                    <Form.Item
                      name={["dimensions", "depth"]}
                      label={t("fixture.depth")}
                      noStyle
                      rules={[
                        { required: true, message: t("fixture.depthRequired") },
                      ]}
                    >
                      <InputNumber
                        placeholder={t("fixture.depthPlaceholder")}
                        addonBefore={t("fixture.depthPlaceholder")}
                        min={0.1}
                        precision={2}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item
                      name={["dimensions", "unit"]}
                      noStyle
                      initialValue="mm"
                    >
                      <Select style={{ width: "100%" }}>
                        <Option value="mm">mm</Option>
                        <Option value="cm">cm</Option>
                        <Option value="m">m</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                {(getNestedFieldError("dimensions", "width") ||
                  getNestedFieldError("dimensions", "height") ||
                  getNestedFieldError("dimensions", "depth")) && (
                  <div className="ant-form-item-explain ant-form-item-explain-error">
                    {getNestedFieldError("dimensions", "width") ||
                      getNestedFieldError("dimensions", "height") ||
                      getNestedFieldError("dimensions", "depth")}
                  </div>
                )}
              </Form.Item>

              <Form.Item
                name="holeCount"
                label={t("fixture.holeCount")}
                rules={[
                  { required: true, message: t("fixture.holeCountRequired") },
                ]}
                validateStatus={
                  getFieldError("holeCount") ? "error" : undefined
                }
                help={getFieldError("holeCount")}
              >
                <InputNumber
                  placeholder={t("fixture.holeCountPlaceholder")}
                  min={0}
                  precision={0}
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item
                name="crossBeamCount"
                label={t("fixture.crossBeamCount")}
                rules={[{ required: true, message: t("fixture.crossBeamCountRequired") }]}
                validateStatus={getFieldError("crossBeamCount") ? "error" : undefined}
                help={getFieldError("crossBeamCount")}
              >
                <InputNumber
                  placeholder={t("fixture.crossBeamCountPlaceholder")}
                  min={1}
                  precision={0}
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item
                name="supportHold"
                label={t("fixture.supportHold")}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="description"
                label={t("fixture.description")}
                validateStatus={
                  getFieldError("description") ? "error" : undefined
                }
                help={getFieldError("description")}
              >
                <TextArea
                  placeholder={t("fixture.descriptionPlaceholder")}
                  rows={4}
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="imageUrl"
                label={t("fixture.image")}
                // rules={[
                //         { required: true, message: t("fixture.imageRequired") },
                //       ]}
                // validateStatus={getFieldError("image") ? "error" : undefined}
                help={getFieldError("image")}
              >
                {/* <ImageUpload
                  value={imageFile}
                  onChange={handleImageChange}
                  maxSize={2}
                  placeholder={
                    imagePreview ? undefined : t("fixture.imagePlaceholder")
                  }
                /> */}

                <Upload
                  // value={imageFile}
                  // onChange={handleImageChange}
                  maxCount={1}
                  // placeholder={
                  //   imagePreview ? undefined : t("fixture.imagePlaceholder")
                  // }
                />
                {/* {imagePreview && (
                  <div style={{ marginTop: "8px" }}>
                    <Image
                      src={imagePreview}
                      alt={t("fixture.imagePreview")}
                      width={200}
                      height={150}
                    />
                  </div>
                )} */}
              </Form.Item>
            </Col>
          </Row>

          <Form.Item className="fixture-form-actions">
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading || isValidating}
              >
                {mode === "create" ? t("common.add") : t("common.save")}
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={handleCancel}
                disabled={loading || isValidating}
              >
                {t("common.cancel")}
              </Button>
            </Space>
          </Form.Item>
        </Card>
      </Form>
    </div>
  );
};

export default FixtureForm;